import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Component } from "react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { ErrorState, errorCopy } from "@/components/states";
import { isApiError } from "@/lib/api/errors";
import { reportError, type ErrorReportContext } from "@/lib/error-reporting";

type RouteErrorBoundaryProps = {
  children: ReactNode;
  routeName?: string;
};

type InnerProps = {
  children: ReactNode;
  boundaryName: string;
  reportContext: ErrorReportContext;
  resetKey: string;
  onFatalNetwork: () => void;
};

type InnerState = {
  error: Error | null;
};

/**
 * Class component that catches render errors and delegates the fallback UI
 * to `<ErrorState variant="page" />` per states-pattern §11 Q6.
 *
 * When the caught error is an `ApiError` of kind `network` and the browser
 * reports offline, the fallback triggers `onFatalNetwork` to redirect to
 * `/offline`.
 */
class RouteErrorBoundaryInner extends Component<InnerProps, InnerState> {
  state: InnerState = { error: null };

  static getDerivedStateFromError(error: Error): InnerState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string | null }) {
    reportError(error, {
      boundary: this.props.boundaryName,
      componentStack: errorInfo.componentStack ?? undefined,
      ...this.props.reportContext,
    });
  }

  componentDidUpdate(prevProps: InnerProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
    if (this.state.error && isApiError(this.state.error)) {
      if (
        this.state.error.error.kind === "network" &&
        typeof navigator !== "undefined" &&
        !navigator.onLine
      ) {
        this.props.onFatalNetwork();
      }
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    return <ErrorState message={errorCopy(this.state.error)} retry={this.reset} variant="page" />;
  }
}

export const RouteErrorBoundary = ({ children, routeName }: RouteErrorBoundaryProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Subscribe so a transition online → offline during an active error
  // re-renders this subtree and the boundary can route to /offline.
  useOnlineStatus();

  return (
    <RouteErrorBoundaryInner
      boundaryName={`route:${routeName ?? location.pathname}`}
      reportContext={{ route: location.pathname }}
      resetKey={location.pathname}
      onFatalNetwork={() => {
        if (location.pathname !== "/offline") {
          navigate("/offline", { replace: true });
        }
      }}
    >
      {children}
    </RouteErrorBoundaryInner>
  );
};
